/**
 * PiecePopups — PIECE GUARD §7, the exact wording.
 *
 * Plain language. No jargon. No system terms. Every string here is the spec's, because
 * the wording IS the feature: "this looks like a packet was entered as 1 piece" is a
 * one-tap fix, while "price deviates 300% from benchmark" is a puzzle that gets clicked
 * past.
 *
 * THE SYSTEM NEVER AUTO-CORRECTS. Every component in this file PROPOSES; a human
 * confirms; the form then resubmits the changed number, which re-enters the same gate
 * as fresh input. A silent ×4 that guesses wrong creates stock that does not physically
 * exist, and nobody ever finds out — that is strictly worse than the original bug.
 */

import React, { useState } from 'react';

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

/**
 * A "No" answer costs one typed line (§14B, confirmed default).
 *
 * The alternative — an optional reason — is faster for staff, but it guts §10's weekly
 * review: a list of overrides with no reasons attached is very hard to act on, and the
 * review is the only thing that catches the person who clicks past everything.
 */
const ReasonBox = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    maxLength={200}
    style={{
      width: '100%', padding: '9px 11px', border: '1px solid #d1d5db',
      borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginTop: 8,
    }}
  />
);

/* ══════════════════════════════════════════════════════════════════════════════
 * POPUP A — Pack mistake suspected.
 * This is the one this entire system exists for.
 * ══════════════════════════════════════════════════════════════════════════════ */
export const PiecePackMistakePopup = ({ blocker, itemName, onResolve, onCancel }) => {
  const [reason, setReason] = useState('');
  const [branch, setBranch] = useState(null); // 'loose' | 'price'

  const {
    entered_quantity: qty,
    entered_total: total,
    price_per_piece: perPiece,
    typical_price_per_piece: typical,
    pieces_per_pack: pack,
    proposed_quantity: proposed,
  } = blocker;

  const reasonOk = reason.trim().length >= 3;

  return (
    <div style={overlay}>
      <div style={modal}>
        <h3 style={h3}>This looks like a packet was entered as {qty === 1 ? '1 piece' : `${qty} pieces`}.</h3>

        <p style={line}>
          You entered: <strong>{qty} piece{qty === 1 ? '' : 's'} of {itemName} for {money(total)}</strong>
        </p>
        <p style={line}>
          That works out to <strong>{money(perPiece)} for one piece.</strong>
        </p>
        <p style={{ ...line, marginTop: 14 }}>
          {itemName} normally comes <strong>{pack} pieces to a packet</strong>, at about{' '}
          <strong>{money(typical)} per piece.</strong>
        </p>

        <p style={{ ...line, marginTop: 16, fontWeight: 700, fontSize: 16 }}>
          Did {qty} packet{qty === 1 ? '' : 's'} ({proposed} pieces) arrive?
        </p>

        {branch === null && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
            {/* One tap, corrected, done. This is the common case and it must be effortless. */}
            <button
              type="button"
              style={primary}
              onClick={() => onResolve({
                type: 'piece_pack_corrected',
                quantity: proposed,
                acknowledgment: `confirmed ${qty} packet(s) of ${pack}`,
              })}
            >
              Yes — save as {proposed} pieces, {money(total)}
            </button>
            <button type="button" style={secondary} onClick={() => setBranch('loose')}>
              No — it really was {qty} loose piece{qty === 1 ? '' : 's'}
            </button>
            <button type="button" style={secondary} onClick={() => setBranch('price')}>
              No — the price has changed
            </button>
          </div>
        )}

        {branch !== null && (
          <div style={{ marginTop: 18 }}>
            <label style={label}>
              {branch === 'loose'
                ? `Why was it ${qty} loose piece${qty === 1 ? '' : 's'}?`
                : 'What changed about the price?'}
            </label>
            <ReasonBox
              value={reason}
              onChange={setReason}
              placeholder={branch === 'loose'
                ? 'e.g. bought loose from the kirana next door'
                : 'e.g. distributor raised the rate from 1 Sept'}
            />
            <p style={hint}>
              This line will be saved exactly as you entered it, kept out of the price
              history until other purchases agree, and shown in the owner’s weekly review.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={!reasonOk}
                style={{ ...primary, opacity: reasonOk ? 1 : 0.5, cursor: reasonOk ? 'pointer' : 'not-allowed' }}
                onClick={() => onResolve({
                  type: branch === 'loose' ? 'piece_loose_confirmed' : 'piece_price_changed',
                  acknowledgment: reason.trim(),
                })}
              >
                Save as entered
              </button>
              <button type="button" style={secondary} onClick={() => { setBranch(null); setReason(''); }}>
                Back
              </button>
            </div>
          </div>
        )}

        <button type="button" style={linkBtn} onClick={onCancel}>Let me fix the number myself</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
 * POPUP B — Price looks unusual (not a pack pattern).
 * ══════════════════════════════════════════════════════════════════════════════ */
export const PiecePriceUnusualPopup = ({ blocker, onResolve, onCancel }) => {
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const reasonOk = reason.trim().length >= 3;

  return (
    <div style={overlay}>
      <div style={modal}>
        <h3 style={h3}>The price looks different from usual.</h3>
        <p style={line}>
          This works out to <strong>{money(blocker.price_per_piece)} per piece.</strong>
        </p>
        <p style={line}>
          You normally pay about <strong>{money(blocker.typical_price_per_piece)} per piece.</strong>
        </p>

        {!confirming ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
            <button type="button" style={primary} onClick={() => setConfirming('price')}>
              The price really has changed
            </button>
            {/* §9.7 — "buy 10, get 1 free" dilutes the per-piece rate downward, which is
                a legitimate reason for a low price and nothing else. Declaring it keeps
                the BILLED rate in the price band: if diluted rates fed the average, a
                year of schemes would drag the benchmark down until real errors stopped
                looking abnormal. Offered only when the price came in LOW — a free-goods
                scheme can never make a price go up. */}
            {Number(blocker.price_per_piece) < Number(blocker.typical_price_per_piece) && (
              <button type="button" style={secondary} onClick={() => setConfirming('scheme')}>
                It was a free-goods scheme (extra pieces came free)
              </button>
            )}
            <button type="button" style={secondary} onClick={onCancel}>
              Let me re-check what I entered
            </button>
          </div>
        ) : (
          <div style={{ marginTop: 18 }}>
            <label style={label}>
              {confirming === 'scheme' ? 'Which scheme was it?' : 'What changed?'}
            </label>
            <ReasonBox
              value={reason}
              onChange={setReason}
              placeholder={confirming === 'scheme'
                ? 'e.g. buy 10 get 1 free — 11 arrived, billed for 10'
                : 'e.g. new rate from this supplier'}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button
                type="button"
                disabled={!reasonOk}
                style={{ ...primary, opacity: reasonOk ? 1 : 0.5, cursor: reasonOk ? 'pointer' : 'not-allowed' }}
                onClick={() => onResolve({
                  type: confirming === 'scheme' ? 'piece_scheme_declared' : 'piece_price_changed',
                  acknowledgment: reason.trim(),
                })}
              >
                Save as entered
              </button>
              <button type="button" style={secondary} onClick={() => setConfirming(false)}>Back</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
 * POPUP C — Odd count, no price history yet.
 * ══════════════════════════════════════════════════════════════════════════════ */
export const PieceOddCountPopup = ({ blocker, itemName, onResolve, onCancel }) => {
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const reasonOk = reason.trim().length >= 3;

  return (
    <div style={overlay}>
      <div style={modal}>
        <h3 style={h3}>{itemName} usually comes in packets of {blocker.pieces_per_pack}.</h3>
        <p style={line}>You entered <strong>{blocker.entered_quantity} pieces.</strong></p>

        {!confirming ? (
          <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
            <button type="button" style={primary} onClick={() => setConfirming(true)}>
              Yes — {blocker.entered_quantity} loose pieces
            </button>
            <button type="button" style={secondary} onClick={onCancel}>
              Let me fix the number
            </button>
          </div>
        ) : (
          <div style={{ marginTop: 18 }}>
            <label style={label}>Why loose?</label>
            <ReasonBox value={reason} onChange={setReason} placeholder="e.g. topped up from the local shop" />
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button
                type="button"
                disabled={!reasonOk}
                style={{ ...primary, opacity: reasonOk ? 1 : 0.5, cursor: reasonOk ? 'pointer' : 'not-allowed' }}
                onClick={() => onResolve({ type: 'piece_loose_confirmed', acknowledgment: reason.trim() })}
              >
                Save as entered
              </button>
              <button type="button" style={secondary} onClick={() => setConfirming(false)}>Back</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
 * Zero-price lines (§3.3).
 *
 * Free samples and replacements are real, but with a total of ₹0 the money check is
 * mathematically impossible — there is nothing to divide. So the line must be TAGGED
 * and explained: an untagged zero would either crash the division or quietly drag the
 * price average toward zero, which is worse than either.
 *
 * Without this popup the gate's refusal would be a dead end — it tells the person to
 * mark the line as free, and this is where they do it.
 * ══════════════════════════════════════════════════════════════════════════════ */
export const PieceFreeLinePopup = ({ blocker, itemName, onResolve, onCancel }) => {
  const [reason, setReason] = useState('');
  const reasonOk = reason.trim().length >= 3;

  return (
    <div style={overlay}>
      <div style={modal}>
        <h3 style={h3}>{itemName} has no price on this line.</h3>
        <p style={line}>
          If it arrived free — a sample, or a replacement for something damaged — say so
          and it will be recorded into stock without touching your usual prices.
        </p>
        <label style={{ ...label, marginTop: 16 }}>Why was there no charge?</label>
        <ReasonBox value={reason} onChange={setReason} placeholder="e.g. replacement for the crushed carton" />
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={!reasonOk}
            style={{ ...primary, opacity: reasonOk ? 1 : 0.5, cursor: reasonOk ? 'pointer' : 'not-allowed' }}
            onClick={() => onResolve({ type: 'piece_free_replacement', acknowledgment: reason.trim() })}
          >
            Save as free / replacement
          </button>
          <button type="button" style={secondary} onClick={onCancel}>
            Let me enter what was paid
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
 * POPUP D — Bulk bills (3 or more flags).
 *
 * They must NEVER fire one after another. On a 60-line distributor bill, fifteen popups
 * in a row means that by the fourth one the person is clicking whatever makes it
 * disappear. Nothing saves; one review screen appears instead.
 *
 * TWO BUTTONS ARE DELIBERATELY ABSENT: "Accept All" and "Skip All". An "Accept All"
 * button would destroy this system within one month of shipping. Do not add one.
 * ══════════════════════════════════════════════════════════════════════════════ */
export const PieceBulkReview = ({ review, items, resolvedIndexes, onPick, onCancel }) => {
  /**
   * M5.3 — ONE QUESTION PER ITEM, not per row.
   *
   * A bill often lists the same item twice (two batches, two expiry dates). Each row
   * raised its own blocker, so the person met the same profile screen twice for the same
   * item — and each save wrote a NEW SUPERSEDING VERSION of the profile, moving
   * effective_from and resetting its clean-purchase count to zero. Answering carefully
   * made the item less trusted than ignoring it.
   *
   * Rows are grouped by item, the question is asked once, and answering it marks every
   * row of that item done. Rows with no item_id (brand-new items) group by name, which is
   * the only identity they have.
   */
  const keyOf = (index) => {
    const it = items[index] || {};
    return it.item_id != null ? `id:${it.item_id}` : `name:${String(it.item_name || index).toLowerCase()}`;
  };
  const resolvedKeys = new Set(resolvedIndexes.map(keyOf));

  const groups = [];
  const seen = new Map();
  for (const entry of review.entries) {
    const k = keyOf(entry.index);
    if (seen.has(k)) { seen.get(k).rows.push(entry.index); continue; }
    const g = { entry, key: k, rows: [entry.index] };
    seen.set(k, g);
    groups.push(g);
  }

  const total = groups.length;
  const done = groups.filter(g => resolvedKeys.has(g.key)).length;
  const allDone = done >= total;

  return (
    <div style={overlay}>
      <div style={{ ...modal, maxWidth: 680 }}>
        <h3 style={h3}>{total} item{total === 1 ? '' : 's'} need a quick check before saving</h3>
        <p style={{ ...hint, marginTop: 0 }}>Sorted by value — biggest amounts first</p>

        <div style={{
          margin: '14px 0', padding: '8px 12px', background: '#f3f4f6',
          borderRadius: 8, fontSize: 14, fontWeight: 600,
        }}>
          Progress: {done} of {total} done
        </div>

        <div style={{ maxHeight: '46vh', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          {groups.map(({ entry, key, rows }) => {
            const item = items[entry.index] || {};
            const isDone = resolvedKeys.has(key);
            return (
              <div
                key={entry.index}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: 12, padding: '11px 14px', borderBottom: '1px solid #f3f4f6',
                  background: isDone ? '#f0fdf4' : '#fff',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {isDone ? '✓ ' : ''}{item.item_name || `Item ${entry.index + 1}`}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                    {item.quantity} pcs · {money(item.total_price)}
                    {rows.length > 1 && (
                      <span> · asked once for all {rows.length} lines of this item</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  style={isDone ? secondary : primary}
                  onClick={() => onPick(entry)}
                >
                  {isDone ? 'Change' : 'Check'}
                </button>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
          {/* Save stays disabled until every one is resolved. */}
          <button
            type="button"
            disabled={!allDone}
            style={{ ...primary, opacity: allDone ? 1 : 0.5, cursor: allDone ? 'pointer' : 'not-allowed' }}
            onClick={() => onPick(null)}
          >
            {allDone ? 'Save the bill' : `${total - done} still to check`}
          </button>
          {/* M5.2 — this used to DISCARD every answer already given. On a fifteen-flag
              bill, hitting one unresolvable row at #15 threw away fourteen answers and
              fourteen typed reasons. The answers now live in the form's own state
              (StockInForm.answeredConfirmations) and are sent again on the next submit, so
              stepping out to fix a row costs the row, not the afternoon. */}
          <button type="button" style={secondary} onClick={onCancel}>
            Back to the form{done > 0 ? ` — your ${done} answer${done === 1 ? '' : 's'} are kept` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Routes one blocker to its popup. Keeping the mapping in one place means a new
 * blocker code cannot quietly end up with no UI at all — it will render the fallback
 * rather than silently vanishing, which is how a check becomes invisible.
 */
export const PiecePopupFor = ({ blocker, items, onResolve, onCancel }) => {
  if (!blocker) return null;
  const itemName = (items[blocker.index] || {}).item_name || 'This item';

  switch (blocker.code) {
    case 'piece_pack_mistake_suspected':
      return <PiecePackMistakePopup blocker={blocker} itemName={itemName} onResolve={onResolve} onCancel={onCancel} />;
    case 'piece_price_unusual':
      return <PiecePriceUnusualPopup blocker={blocker} onResolve={onResolve} onCancel={onCancel} />;
    case 'piece_count_not_multiple':
      return <PieceOddCountPopup blocker={blocker} itemName={itemName} onResolve={onResolve} onCancel={onCancel} />;
    case 'piece_zero_price_untagged':
    case 'piece_free_reason_required':
      return <PieceFreeLinePopup blocker={blocker} itemName={itemName} onResolve={onResolve} onCancel={onCancel} />;
    // ── M5.1 — THE CODES THAT HAD NO WAY OUT ─────────────────────────────────
    // `piece_decimal_quantity`, `piece_invalid_quantity` and
    // `piece_override_reason_required` all fell to the fallback below, whose only control
    // was "Back to the form". Inside Popup D that is a DEAD END: the entry can never be
    // marked done, so the Save button stays disabled for ever with no route forward. On a
    // fifteen-flag bill, one of these at #15 made the whole bill unsaveable.
    // S8 §9.8 — the weight cross-check. A SECOND, independent dimension: the money check
    // compares a purchase against the item's own history and is therefore blind when that
    // history is itself wrong, which is exactly §9.3's self-sealing first mistake. A
    // weight comes from the bill, not from the history, so it can see what money cannot.
    case 'piece_weight_mismatch':
      return <PieceWeightMismatchPopup blocker={blocker} itemName={itemName} onResolve={onResolve} onCancel={onCancel} />;
    case 'piece_decimal_quantity':
      return <PieceDecimalQuantityPopup blocker={blocker} itemName={itemName} onResolve={onResolve} onCancel={onCancel} />;
    case 'piece_invalid_quantity':
      return <PieceInvalidQuantityPopup blocker={blocker} itemName={itemName} onCancel={onCancel} />;
    case 'piece_override_reason_required':
      return <PieceReasonRequiredPopup blocker={blocker} itemName={itemName} onResolve={onResolve} onCancel={onCancel} />;
    default:
      return (
        <div style={overlay}>
          <div style={modal}>
            <h3 style={h3}>{itemName}</h3>
            <p style={line}>{blocker.message}</p>
            <button type="button" style={secondary} onClick={onCancel}>Back to the form</button>
          </div>
        </div>
      );
  }
};

/**
 * S8 §9.8 — "the weight does not add up".
 *
 * The proposal exists only when the weight implies a WHOLE number of pieces; "4.37 pieces"
 * is a quantity that cannot have arrived, and offering it as a one-tap answer would be the
 * auto-correct this module refuses to have anywhere.
 */
export const PieceWeightMismatchPopup = ({ blocker, itemName, onResolve, onCancel }) => {
  const [reason, setReason] = React.useState('');
  const proposal = Number(blocker.proposed_quantity);
  const canPropose = Number.isInteger(proposal) && proposal > 0;
  const ok = reason.trim().length >= 3;
  return (
    <div style={overlay}>
      <div style={modal}>
        <h3 style={h3}>{itemName}: the weight does not add up</h3>
        <p style={line}>{blocker.message}</p>
        <p style={hint}>
          One {blocker.piece_description || 'piece'} is recorded as {blocker.piece_weight_g} g.
          {' '}That is what makes this a check the price cannot do: it comes from the bill,
          {' '}not from what this item usually costs.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
          {canPropose && (
            <button
              type="button"
              style={primary}
              onClick={() => onResolve({ type: 'piece_pack_corrected', quantity: proposal })}
            >
              Yes — <strong>{proposal}</strong> piece{proposal === 1 ? '' : 's'} arrived
            </button>
          )}
          <label style={{ ...label, marginTop: 4 }}>No — the count is right. Why?</label>
          <input
            type="text"
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. these are the large bars, not the standard ones"
            style={{
              width: '100%', padding: '9px 11px', boxSizing: 'border-box',
              border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14,
            }}
          />
          <button
            type="button"
            disabled={!ok}
            style={{ ...secondary, opacity: ok ? 1 : 0.5, cursor: ok ? 'pointer' : 'not-allowed' }}
            onClick={() => onResolve({ type: 'piece_loose_confirmed', acknowledgment: reason.trim() })}
          >
            Save as entered with this reason
          </button>
          <button type="button" style={linkBtn} onClick={onCancel}>Let me fix the row myself</button>
        </div>
      </div>
    </div>
  );
};

/**
 * M5.1 — a decimal piece count, with a way to fix it from here.
 *
 * "2.5 Kit Kats" means the person is thinking in weight, and the honest answers are
 * "round it" or "I'll fix the unit myself". Rounding is a PROPOSAL that goes back through
 * the gate as fresh input, exactly like every other correction in this module — nothing
 * here writes the rounded number itself.
 */
export const PieceDecimalQuantityPopup = ({ blocker, itemName, onResolve, onCancel }) => {
  const entered = Number(blocker.entered_quantity);
  const down = Math.floor(entered);
  const up = Math.ceil(entered);
  return (
    <div style={overlay}>
      <div style={modal}>
        <h3 style={h3}>{itemName}: pieces can't have decimals</h3>
        <p style={line}>{blocker.message}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
          {down >= 1 && (
            <button
              type="button"
              style={primary}
              onClick={() => onResolve({ type: 'piece_pack_corrected', quantity: down })}
            >
              It was <strong>{down}</strong> piece{down === 1 ? '' : 's'}
            </button>
          )}
          {up !== down && (
            <button
              type="button"
              style={primary}
              onClick={() => onResolve({ type: 'piece_pack_corrected', quantity: up })}
            >
              It was <strong>{up}</strong> piece{up === 1 ? '' : 's'}
            </button>
          )}
          <button type="button" style={secondary} onClick={onCancel}>
            It is sold by weight — I'll change the unit on the form
          </button>
        </div>
        <p style={hint}>
          Whichever you choose is sent back through the same check as a fresh entry. Nothing
          is rounded on your behalf.
        </p>
      </div>
    </div>
  );
};

/** M5.1 — an unusable quantity. There is nothing to propose; say so and point at the row. */
export const PieceInvalidQuantityPopup = ({ blocker, itemName, onCancel }) => (
  <div style={overlay}>
    <div style={modal}>
      <h3 style={h3}>{itemName}</h3>
      <p style={line}>{blocker.message}</p>
      <p style={hint}>
        This one can only be fixed on the row itself — there is no number here to propose.
      </p>
      <button type="button" style={secondary} onClick={onCancel}>Back to the form</button>
    </div>
  </div>
);

/**
 * M5.1 — §14B's typed reason, collected HERE.
 *
 * The gate raises this when an override arrived without one. It used to have no UI, so
 * the person was told a reason was required and given nowhere to type it.
 */
export const PieceReasonRequiredPopup = ({ blocker, itemName, onResolve, onCancel }) => {
  const [reason, setReason] = React.useState('');
  const req = Array.isArray(blocker.requires) ? blocker.requires[0] : blocker.requires;
  const ok = reason.trim().length >= 3;
  return (
    <div style={overlay}>
      <div style={modal}>
        <h3 style={h3}>{itemName}</h3>
        <p style={line}>{blocker.message}</p>
        <label style={{ ...label, marginTop: 14 }}>Why is this right?</label>
        <input
          type="text"
          autoFocus
          value={reason}
          maxLength={200}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. supplier raised the rate this week"
          style={{
            width: '100%', padding: '9px 11px', marginTop: 6, boxSizing: 'border-box',
            border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14,
          }}
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={!ok}
            style={{ ...primary, opacity: ok ? 1 : 0.5, cursor: ok ? 'pointer' : 'not-allowed' }}
            onClick={() => onResolve({ type: req?.type || 'piece_loose_confirmed', acknowledgment: reason.trim() })}
          >
            Save with this reason
          </button>
          <button type="button" style={secondary} onClick={onCancel}>Back to the form</button>
        </div>
        <p style={hint}>
          The owner reads these in the weekly review. One honest line is worth more than a
          perfect one.
        </p>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
 * "IS IT STILL THE SAME PACKET?" — the repeat-purchase check.
 *
 * Only PACKET items ever see this, and only once per item per bill. An item somebody
 * declared as arriving in single pieces is never asked: there is no packet size that
 * could have changed.
 *
 * A pack size is the one fact in this whole system that is TRUE WHEN RECORDED AND FALSE
 * LATER without anybody doing anything wrong. The supplier moves Kit Kat from a 4-pack to
 * a 6-pack, the price per packet moves with it, and every row entered afterwards is
 * silently counted against a pack that no longer exists — the profile keeps insisting on
 * 4 and the money check keeps "confirming" numbers built on it. Nothing in the ladder can
 * catch that, because the ladder's yardstick is the stale fact itself.
 *
 * So the yardstick is read back to a human the next time they buy the thing: this is what
 * you said last time, and this is what you paid. Continue, or stop and fix the row. Two
 * buttons, no typing, no reason box — this is a confirmation, not an override, and
 * charging for it in keystrokes is how it would get clicked past.
 *
 * ── THE SECOND BUTTON FILLS THE QUANTITY IN ───────────────────────────────────
 * It used to say "Cancel — let me change it", close, and leave the person looking at the
 * row wondering what to type. The overwhelmingly common cause of the rate moving by a
 * clean multiple is that PACKETS were typed where PIECES were wanted, and the corrected
 * number is not a judgement call — it is `entered x pieces-per-packet`, arithmetic this
 * popup has both halves of. So it offers it, with the working shown on the button.
 *
 * This is NOT the auto-correct §7 forbids. Nothing is saved: the number lands in the
 * quantity box, the submission stops, and the person looks at their own row and presses
 * Submit themselves. It proposes; a human still confirms. The difference from before is
 * that the proposal is a filled-in field instead of a sentence they have to act on.
 * ══════════════════════════════════════════════════════════════════════════════ */
export const PiecePackConfirmPopup = ({ entry, remaining = 0, onContinue, onCancel, onFixQuantity }) => {
  const pack = Number(entry?.pieces_per_pack) || 0;
  const perPiece = Number(entry?.last_price_per_piece);
  const qty = Number(entry?.quantity);
  const total = Number(entry?.total_price);
  const now = Number.isFinite(Number(entry?.now_price_per_piece))
    ? Number(entry.now_price_per_piece)
    : (qty > 0 ? total / qty : NaN);
  const movedUp = now > perPiece;
  const pct = perPiece > 0 ? Math.round(Math.abs(now - perPiece) / perPiece * 100) : null;
  const correctedQty = pack >= 2 && Number.isFinite(qty) && qty > 0 ? qty * pack : null;
  const canFix = typeof onFixQuantity === 'function' && correctedQty !== null && correctedQty !== qty;
  const correctedRate = canFix && total > 0 ? total / correctedQty : null;

  return (
    <div style={overlay}>
      <div style={modal}>
        <button type="button" style={closeBtn} onClick={onCancel} aria-label="Close">×</button>

        <h3 style={h3}>
          {entry?.item_name || 'This item'}: price per piece {movedUp ? 'went up' : 'went down'}
          {pct != null && ` (${pct}%)`}.
        </h3>

        <p style={line}>
          Last time: <strong>{money(perPiece)}/piece</strong>.
          This bill: <strong>{money(now)}/piece</strong>.
        </p>

        {canFix && (
          <p style={{ ...hint, marginTop: 8 }}>
            Entered {qty} pieces — did you mean {qty} packets ({correctedQty} pieces total)?
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          {canFix ? (
            <button type="button" style={secondary} onClick={() => onFixQuantity(correctedQty)}>
              Change it — {qty}×{pack} = {correctedQty} pcs
              {correctedRate != null && (
                <span style={{ display: 'block', fontWeight: 400, fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  {money(correctedRate)}/piece
                </span>
              )}
            </button>
          ) : (
            <button type="button" style={secondary} onClick={onCancel}>
              Let me fix it
            </button>
          )}
          <button type="button" style={primary} onClick={onContinue}>
            Yes, continue
          </button>
        </div>

        {remaining > 0 && (
          <p style={{ ...hint, marginTop: 14 }}>{remaining} more item{remaining === 1 ? '' : 's'} to confirm.</p>
        )}
      </div>
    </div>
  );
};

// ── styles ────────────────────────────────────────────────────────────────────
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: 16,
};
const modal = {
  position: 'relative',
  background: '#fff', borderRadius: 16, padding: '28px 24px 24px', width: '100%',
  maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const closeBtn = {
  position: 'absolute', top: 14, right: 14,
  background: 'none', border: 'none', fontSize: 22, lineHeight: 1,
  color: '#9ca3af', cursor: 'pointer', padding: '2px 6px', borderRadius: 6,
};
const h3 = { margin: '0 0 14px', fontSize: 17, fontWeight: 700, color: '#111827', lineHeight: 1.35, paddingRight: 28 };
const line = { margin: '6px 0', fontSize: 14.5, color: '#374151', lineHeight: 1.5 };
const label = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151' };
const hint = { fontSize: 12.5, color: '#6b7280', marginTop: 10, lineHeight: 1.5 };
const primary = {
  flex: 1,
  padding: '9px 16px', background: '#111827', color: '#fff', border: 'none',
  borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', textAlign: 'center',
};
const secondary = {
  flex: 1,
  padding: '9px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', textAlign: 'center',
};
const linkBtn = {
  marginTop: 16, background: 'none', border: 'none', color: '#6b7280',
  fontSize: 13, textDecoration: 'underline', cursor: 'pointer', padding: 0,
};

export default PiecePopupFor;

